import { useEffect, useRef, useState } from "react";
import { logExperience } from "@/lib/experience";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import TheField from "@/components/TheField";
import { Button } from "@/components/ui/button";
import { Play, Pause, ArrowLeft, Loader2, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import SleepDim, { useSleepNight } from "@/components/SleepDim";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Installation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [affIdx, setAffIdx] = useState(0);
  const [shifted, setShifted] = useState(false);
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "sleep" ? "sleep" : "day");
  const mediaRef = useRef(null);

  useEffect(() => { api.get(`/installations/${id}`).then((r) => setInst(r.data)).catch(() => navigate("/inner")); }, [id, navigate]);
  useEffect(() => () => { if (mediaRef.current) mediaRef.current.pause(); }, []);
  useEffect(() => {
    const affs = inst?.affirmations || [];
    if (!playing || !affs.length) return;
    const t = setInterval(() => setAffIdx((x) => (x + 1) % affs.length), 4500);
    return () => clearInterval(t);
  }, [playing, inst]);
  useSleepNight(mode === "sleep" && playing && !!inst?.sleep_audio_url, id);

  if (!inst) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const isVideo = inst.media_url && inst.media_type === "video";
  const affs = inst.affirmations || [];

  const attach = (audio) => {
    mediaRef.current = audio;
    audio.ontimeupdate = () => setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    audio.onended = () => { setPlaying(false); setProgress(0); setShifted(true); logExperience("installation", id, inst?.title || ""); };
  };

  const toggle = async () => {
    if (playing) { mediaRef.current?.pause(); setPlaying(false); return; }
    if (mediaRef.current) { mediaRef.current.play(); setPlaying(true); return; }
    setLoading(true);
    try {
      let src;
      if (mode === "sleep" && inst.sleep_audio_url) src = `${BACKEND_URL}${inst.sleep_audio_url}`;
      else if (inst.long_audio_url) src = `${BACKEND_URL}${inst.long_audio_url}`;
      else if (inst.media_url) src = `${BACKEND_URL}${inst.media_url}`;
      else { const { data } = await api.post("/audio/guided", { text: inst.script, kind: "installation", ref_id: inst.id }); src = `${BACKEND_URL}${data.url}`; }
      const audio = new Audio(src); attach(audio); await audio.play(); setPlaying(true);
    } catch { toast.error("Could not begin the installation."); } finally { setLoading(false); }
  };

  const intensity = 0.4 + progress * 0.6;
  const switchMode = (m) => {
    if (m === mode) return;
    mediaRef.current?.pause(); mediaRef.current = null; setPlaying(false); setProgress(0); setShifted(false); setMode(m);
  };

  if (isVideo) {
    return (
      <div className="min-h-screen bg-black relative">
        <button onClick={() => navigate("/inner")} className="absolute top-6 left-6 z-20 flex items-center gap-1 text-sm text-white/80 hover:text-white" data-testid="installation-back"><ArrowLeft className="w-4 h-4" /> Library</button>
        <video src={`${BACKEND_URL}${inst.media_url}`} controls autoPlay loop playsInline onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="w-full h-screen object-cover" data-testid="video-player" />
        <AnimatePresence>
          {playing && affs.length > 0 && (
            <motion.p key={affIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="absolute bottom-24 inset-x-0 text-center font-serif text-3xl italic text-white px-6 drop-shadow-lg pointer-events-none">"{affs[affIdx]}"</motion.p>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative grain overflow-hidden">
      <img src={inst.cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
      <div className={`absolute inset-0 transition-colors duration-1000 ${mode === "sleep" ? "bg-[#03050b]/90" : "bg-background/70"}`} />
      <SleepDim active={mode === "sleep" && playing} />
      <div className="relative z-10 max-w-2xl mx-auto px-5 py-8 min-h-screen flex flex-col">
        <button onClick={() => navigate("/inner")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6" data-testid="installation-back"><ArrowLeft className="w-4 h-4" /> Library</button>
        <div className="text-center mb-6"><p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">{inst.theme} · {mode === "sleep" && inst.sleep_duration ? inst.sleep_duration : inst.duration}</p><h1 className="font-serif text-4xl sm:text-5xl">{inst.title}</h1>
          {inst.sleep_audio_url && (
            <div className="inline-flex mt-5 rounded-full border border-border p-1 bg-background/40" data-testid="installation-mode">
              <button onClick={() => switchMode("day")} data-testid="installation-mode-day" className={`px-4 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${mode === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Sun className="w-3.5 h-3.5" /> Day</button>
              <button onClick={() => switchMode("sleep")} data-testid="installation-mode-sleep" className={`px-4 py-1.5 rounded-full text-xs flex items-center gap-1.5 transition-colors ${mode === "sleep" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}><Moon className="w-3.5 h-3.5" /> Sleep</button>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center justify-center pb-12">
          <button onClick={toggle} disabled={loading} data-testid="audio-toggle" className="mb-10 rounded-full transition-transform duration-300 hover:scale-105">
            <TheField intensity={intensity} size={280} active={playing}>
              <span className="w-24 h-24 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl">
                {loading ? <Loader2 className="w-8 h-8 animate-spin" /> : playing ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" fill="currentColor" />}
              </span>
            </TheField>
          </button>
          <div className="h-16 flex items-center justify-center">
            <AnimatePresence mode="wait">
              {playing && affs.length > 0 ? (
                <motion.p key={affIdx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="font-serif text-2xl sm:text-3xl italic text-foreground max-w-lg text-center">"{affs[affIdx]}"</motion.p>
              ) : (
                <p className="text-muted-foreground max-w-md text-center">{inst.description}</p>
              )}
            </AnimatePresence>
          </div>
          {shifted && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center mt-6" data-testid="field-shifting">
              <p className="text-xs uppercase tracking-[0.2em] text-primary mb-1">Your Field is shifting</p>
              <p className="font-serif text-2xl italic">Less seeking. More presence.</p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
